import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import { useTranslation } from 'react-i18next';
import { Steps, Button, Form, Input, Modal, Card, Tooltip, Row, Col, List, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { QuestionCircleOutlined } from '@ant-design/icons';
import './PrincipalForm.css';
import { useUser } from '../../providers/UserContext';

const { Step } = Steps;

const PrincipalForm = ({ setHasIdNegocio }) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('1');
  const [businessId, setBusinessId] = useState(null);
  const { notifyAuthCompleted } = useUser();

  const isMobile = window.innerWidth <= 768;

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const apiUrl = process.env.REACT_APP_API_URL;

  const { user} = useUser();

  const plansOptions = [
    {
      id: '1',
      title: t('basicPlan'),
      description: [
        'Gráficos con información sobre tu negocio',
        'Comparativas con otros negocios cercanos',
        'Informes personalizados de tu negocio (hasta 3)',
        'Asistente ChatBot',
      ],
      imgSrc: '../../../assets/images/BasicPlan_Image.jpg',
      price: t('basicPrice'),
    },
    {
      id: '2',
      title: t('premiumPlan'),
      description: [
        'Gráficos con información sobre tu negocio',
        'Comparativas con otros negocios cercanos',
        'Informes personalizados de tu negocio (ilimitados)',
        'Asistente ChatBot',
      ],
      imgSrc: '../../../assets/images/PremiumPlan_Image.jpg',
      price: t('premiumPrice'),
    },
  ];

  const searchBusinesses = async (businessName) => {
    if (!businessName || businessName.trim() === '') {
      setSearchResults([]);
      return;
    }
  
    setIsSearching(true);
    try {
      const response = await axios.get(`${apiUrl}/collections/search-businesses`, {
        params: { name: businessName }
      });
      
      // Asignar id único a cada negocio en el resultado
      const resultsWithId = response.data.map((business, index) => ({
        ...business,
        id: index 
      }));
  
      setSearchResults(resultsWithId);
    } catch (error) {
      console.error('Error al buscar negocios:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };  

  const handleBusinessSelect = (business) => {
    console.log('Selected business:', business); // Para verificar qué negocio estás seleccionando
    setSelectedBusiness(business); // Esto debe actualizar correctamente el estado del negocio seleccionado
    // form.setFieldsValue({ nombreNegocio: business.nombre });
  };

  useEffect(() => {
    const allErrors = form.getFieldsError();
    const fieldsWithErrors = allErrors.filter(({ errors }) => errors.length > 0);

    if (fieldsWithErrors.length > 0) {
        const fieldsToValidate = fieldsWithErrors.map(({ name }) => name[0]);
        form.validateFields(fieldsToValidate);
    }
  }, [i18n.language]);

  const handleNext = async () => {
    try {
      if (currentStep === 0) {
        // Validar que se ha seleccionado un negocio
        await form.validateFields(['nombreNegocio']);
  
        // Verificar que hay un negocio seleccionado
        if (!selectedBusiness || !selectedBusiness.id_negocio) {
          console.error('No se ha seleccionado un negocio');
          return;
        }
  
        // Guardamos el id_negocio del negocio seleccionado en el estado
        setBusinessId(selectedBusiness.id_negocio);
      } else if (currentStep === 1) {
        // Validar campos del paso 2
        await form.validateFields(['nombreUsuario', 'mailContacto', 'telefono']);
  
        // Obtener el usuario actual
        let usuario = auth.currentUser;
  
        console.log("USUARIO PRINCIPAL FORM: ", usuario)
        // Si no hay usuario logueado, obtenerlo del localStorage
        if (!usuario) {
          const userUID = localStorage.getItem('userUID');
          const { data: userFromApi } = await axios.get(`${apiUrl}/collections/user/${userUID}`);
          usuario = userFromApi;
        }
  
        const today = new Date();
        const formattedDate = `${today.getDate()}/${today.getMonth() + 1}/${today.getFullYear()}`;
  
        const values = form.getFieldsValue();
  
        console.log('Current user:', usuario);
        console.log('Form values:', values);
  
        if (!usuario || !usuario.email) {
          console.error('No user is currently logged in or email is missing');
          return;
        }
  
        // Preparamos los datos a enviar con el id_negocio del estado (businessId)
        const dataToSend = {
          email: usuario.email,
          id_negocio: businessId,
          nombre_usuario: values.nombreUsuario,
          telefono: values.telefono,
          mail_contacto: values.mailContacto,
          direccion: values.direccion,
          is_premium: false,
          date: formattedDate,
          reports: 0,
        };
  
        console.log('Data being sent to API:', dataToSend);
  
        try {
          // Llamada a la API para actualizar el usuario
          const response = await axios.post(`${apiUrl}/collections/update-user-date`, dataToSend);
          notifyAuthCompleted();
          console.log('API response:', response.data);
        } catch (error) {
          console.error('Error in API call:', error.response ? error.response.data : error.message);
          return;
        }
      }
  
      // Avanzar al siguiente paso
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Validation or step processing failed:', error);
    }
  };
  

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handlePlanChange = (planId) => {
    setSelectedPlan(planId);
    if (planId === '2') {
      setModalVisible(true);
    }
  };

  const handleFinish = async () => {
    try {
      const values = form.getFieldsValue();
  
      // Obtener el usuario actual
      let usuario = auth.currentUser;
  
      // Si no hay usuario logueado, obtenerlo del localStorage
      if (!usuario) {
        const userUID = localStorage.getItem('userUID');
        const { data: userFromApi } = await axios.get(`${apiUrl}/collections/user/${userUID}`);
        usuario = userFromApi;
      }
  
      const today = new Date();
      const formattedDate = `${today.getDate()}/${today.getMonth() + 1}/${today.getFullYear()}`;
  
      if (!usuario || !usuario.email) {
        console.error('No user is currently logged in or email is missing');
        return;
      }
  
      const { email, username } = usuario;
  
      const dataToSend = {
        email,
        id_negocio: businessId,
        nombre_negocio: selectedBusiness.nombre,
        nombre_usuario: values.nombreUsuario,
        telefono: values.telefono,
        mail_contacto: values.mailContacto,
        direccion: values.direccion,
        is_premium: selectedPlan === '2',
        date: formattedDate,
        reports: 0,
      };
  
      await axios.post(`${apiUrl}/collections/update-user-date`, dataToSend);
  
      user.id_negocio = businessId;
      user.nombre_usuario = values.nombreUsuario;
      user.telefono = values.telefono;
      user.mail_contacto = values.mailContacto;
      user.direccion = values.direccion;
      user.is_premium = selectedPlan === '2';
      user.date = formattedDate;
      user.reports = 0;

      if (selectedPlan === '1') {
        setHasIdNegocio(true);
        navigate('/dashboard');
      } else {
        setModalVisible(true);
      }
    } catch (error) {
      console.error('Error during finish process:', error);
    }
  };

  const handlePayment = async () => {
    try {
      const { data } = await axios.post(`${apiUrl}/collections/create-checkout-session`, {
        userId: auth.currentUser.uid ? auth.currentUser.uid : localStorage.getItem("userUID"),
        userMail: auth.currentUser.email
      });
      window.location.href = data.url;
    } catch (error) {
      console.error('Error iniciando la sesión de pago:', error);
    }
  };

  const steps = [
    {
      title: t('businessSearch'),
      content: (
        <>
          <Form.Item
            name="nombreNegocio"
            label={t('principalFormBusinessName')}
            rules={[{ required: true, message: t('principalFormBusinessNameRequired') }]}
            help={searchResults.length === 0 && !isSearching && form.getFieldValue('nombreNegocio') ? t('noResultsFound') : null}
            validateStatus={searchResults.length === 0 && !isSearching && form.getFieldValue('nombreNegocio') ? 'error' : ''}
          >
            <Input.Search
              className='input-search'
              placeholder={t('principalFormBusinessNamePlaceholder')}
              onSearch={(value) => {
                searchBusinesses(value);  // Pasa el valor de búsqueda a tu función
              }}
              enterButton={<Button icon={<SearchOutlined />}>{t('search')}</Button>}
            />
          </Form.Item>
    
          {/* Mostrar el spinner si está buscando */}
          {isSearching ? (
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <Spin tip={t('searching')} />
            </div>
          ) : (
            <>
              {/* Mostrar resultados si hay */}
              {searchResults.length > 0 && (
                <List
                  itemLayout="horizontal"
                  dataSource={searchResults}
                  renderItem={item => (
                    <List.Item
                      key={item.id}
                      onClick={() => handleBusinessSelect(item)}
                      className={selectedBusiness && selectedBusiness.id === item.id ? 'selected-item' : 'unselected-item'}
                      style={{ cursor: 'pointer' }}
                    >
                      <List.Item.Meta
                        title={item.nombre}
                        description={item.direccion}
                      />
                    </List.Item>
                  )}
                />
              )}
              {searchResults.length > 0 && !isSearching && (
                <div className='noresults-wrapper'>
                  <p> {t('noResultsContact')} <a>{t('noResultsLink')}</a></p>
                </div>
              )}

              {searchResults.length === 0 && !isSearching && form.getFieldValue('nombreNegocio') && (
                <div className='noresults-wrapper'>
                  <p> {t('noResultsContact')} <a href='/contact'>{t('noResultsLink')}</a></p>
                </div>
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: t('userInfo'),
      content: (
        <>
          <Form.Item
            name="nombreUsuario"
            label={t('principalFormUsername')}
            rules={[{ required: true, message: t('principalFormUsernameRequired') }]}
          >
            <Input placeholder={t('principalFormUsernamePlaceholder')} />
          </Form.Item>
          <Form.Item 
            name="telefono" 
            label={t('principalFormPhone')}
            rules={[
              {
                pattern: /^[0-9]{9}$/,
                message: t('principalFormPhoneInvalid'),
              }
            ]}
          >
            <Input placeholder={t('principalFormPhonePlaceholder')} />
          </Form.Item>
          <Form.Item
            name="mailContacto"
            label={t('principalFormEmail')}
            rules={[
              { required: true, message: t('principalFormEmailRequired') },
              { type: 'email', message: t('principalFormEmailInvalid') }
            ]}
          >
            <Input placeholder={t('principalFormEmailPlaceholder')} />
          </Form.Item>
          <Form.Item name="direccion" label={t('principalFormAddress')}>
            <Input placeholder={t('principalFormAddressPlaceholder')} />
          </Form.Item>
        </>
      ),
    },
    {
      title: t('selectPlan'),
      content: (
        <Row gutter={16} justify="center">
          {plansOptions.map((plan) => (
            <Col xs={24} sm={12} key={plan.id} style={{display:'flex', justifyContent: 'center'}}>
              <Card
                hoverable
                className={`card-custom ${selectedPlan === plan.id ? 'selected' : ''}`}
                cover={<img alt={plan.title} src={plan.imgSrc} style={{ height: '260px', objectFit: 'cover' }} />}
                onClick={() => handlePlanChange(plan.id)}
              >
                <Card.Meta
                  title={plan.title}
                  description={
                    <div style={{ height: 'auto', overflowY: 'auto' }}>
                      <ul className="list-unstyled">
                        {plan.description.map((item, index) => (
                          <li key={index}>
                            {plan.id === '1' && index % 2 !== 0 ? (
                              <i className='ph-x' style={{ color: 'red', fontSize: '20px', marginRight: '10px' }} />
                            ) : (
                              <i className='ph-check' style={{ color: 'green', fontSize: '24px', marginRight: '10px' }} />
                            )}
                            {item}
                          </li>
                        ))}
                      </ul>
                      <Button className='plans-btn' type="primary" style={{ marginTop: '10px' }}>{plan.price}</Button>
                    </div>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>
      ),
    },
  ];

  return (
    <div className="general-wrapper principal-form">
      <div className="container">
        <Col>
          <Card className='header-card-pf' bodyStyle={{padding: '0'}}>
          <div className={ isMobile ? "div-header-card-pf-mobile" : "div-header-card-pf" }>
            <h6 className="card-title-pf">{t('principalForm')}</h6>
            <small className="card-extra-pf">{t('principalFormBody')}</small>
          </div>
          </Card>
          <Card
            bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
            className='body-card-pf'>
            <div className='steps-wrapper'>
              <Steps current={currentStep}>
                {steps.map(item => <Step key={item.title} title={item.title} />)}
              </Steps>
            </div>
            <div className="steps-content">
              <Form form={form} layout="vertical">
                {steps[currentStep].content}
              </Form>
            </div>
            <div className="steps-action">
              {currentStep > 0 && (
                <Button style={{ margin: '0 8px' }} className='previous-btn' onClick={handlePrev}>
                  {t('previous')}
                </Button>
              )}
              {currentStep < steps.length - 1 && (
                <Button variant="primary" className='next-btn custom-btn'
                  onClick={handleNext}
                  disabled={currentStep === 0 && !selectedBusiness}
                >
                  {t('next')}
                </Button>
              )}
              {currentStep === steps.length - 1 && (
                <Button variant="primary" className='finish-btn custom-btn' onClick={handleFinish}>
                  {t('finish')}
                </Button>
              )}
            </div>
          </Card>
        </Col>
      </div>
      <Modal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        title={t('paymentConfirmationTitle')}
      >
        <p>{t('paymentConfirmationMessage')}</p>
        <div className='paymentButton'>
          <Button variant="primary" className='custom-btn' onClick={handlePayment}>
            {t('proceedToPayment')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default PrincipalForm;